import * as _ from 'lodash';
import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {Store} from '@ngrx/store';
import {EMPTY, Observable, Subscription} from 'rxjs';
import {catchError, switchMap, takeWhile} from 'rxjs/operators';

import {ApiRoutes} from '../../../common/api-routes';
import {OnError, OnMessage, OnSSEConnected, OnSSEDisconnected} from '../core/actions/notification.action';
import {AuthService} from '../auth/services/auth.service';
import {ClientConfig} from '../../../common/client-config.model';
import {selectIsPaymaster} from '../auth/selectors';
import {getState} from '../shared/utils';
import {Notifications} from '../../../common/notification.model';
import {SsePrimaryTabKey} from '../shared/constants';

/**
 * Requirement: primary browser tab (tab in which the user logs in) will maintain SSE connection open until the user logs out.
 * Other tabs will skip opening SSE connection & listen to the primary browser tab for SSE messages from the server using BroadcastChannel API.
 *
 * EventSource states:
 * 0 - CONNECTING
 * 1 - OPEN
 * 2 - CLOSED
 */
@Injectable()
export class NotificationEventSourceService implements OnDestroy {
  private readonly _isSsePrimaryTab: boolean;
  private _isAlive = true;
  private _eventSource: EventSource;
  private _subscription: Subscription;
  private _clientReconnectTimer = null;

  constructor(private _store: Store, private _authService: AuthService, private _ngZone: NgZone) {
    this._isSsePrimaryTab = this._registerSsePrimaryTab();
  }

  private _openConnection(): void {
    // Initialize a new event source
    const eventSourceObservable = new Observable<Notifications>((observer) => {
      this._eventSource = new EventSource(`${ApiRoutes.Sse}/subscribe`, { withCredentials: true });

      this._eventSource.onmessage = sseMessage => {
        try {
          const payload: Notifications = JSON.parse(sseMessage.data);
          // Wrap in NgZone so Angular triggers change detection
          this._ngZone.run(() => {
            observer.next(payload);
          });
        } catch (parseError) {
          console.warn(`[NotificationEventSourceService] ParseError: ${parseError}`);
        }
      };

      this._eventSource.onerror = error => {
        // Wrap in NgZone so Angular triggers change detection
        this._ngZone.run(() => {
          console.log(`[NotificationEventSourceService] Error: ${JSON.stringify(error)}`);
          this._store.dispatch(OnError(error));
          if (this._eventSource.readyState === EventSource.CLOSED) {
            this._store.dispatch(OnSSEDisconnected());
            console.log(`[NotificationEventSourceService] SSE Connection closed`);
          }
          observer.complete();
        });
      };

      this._eventSource.onopen = () => {
        // Wrap in NgZone so Angular triggers change detection
        this._ngZone.run(() => {
          console.log(`[NotificationEventSourceService] SSE Connection opened`);
          this._store.dispatch(OnSSEConnected());
        });
      };

      // EventSource cleanup
      return () => {
        this._cleanupSSEResources();
      };
    });

    // Remove stale subscriptions
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }

    // Dispatch actions on incoming SSE messages
    this._subscription = eventSourceObservable.pipe(
      takeWhile(() => this._isAlive)
    ).subscribe((message: Notifications) => {
      console.log(`[NotificationEventSourceService] message: ${JSON.stringify(message)}`);
      this._store.dispatch(OnMessage(message));
    });
  }

  private _cleanupSSEResources(): void {
    console.log(`[NotificationEventSourceService] SSE Connection cleanup`);
    // send unsubscribe beacon
    if ('sendBeacon' in navigator) {
      try {
        navigator.sendBeacon(`${ApiRoutes.Sse}/unsubscribe`);
      } catch (error) {
        console.warn('[NotificationEventSourceService] Failed to send disconnect beacon:', error);
      }
    }
    if (this._clientReconnectTimer) {
      clearInterval(this._clientReconnectTimer);
      this._clientReconnectTimer = null;
    }
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }
    if (this._eventSource) {
      this._eventSource.onmessage = null;
      this._eventSource.onerror = null;
      this._eventSource.onopen = null;
      this._eventSource.close();
      this._eventSource = null;
      this._store.dispatch(OnSSEDisconnected());
    }
  }

  private _registerSsePrimaryTab(): boolean {
    if (localStorage.getItem(SsePrimaryTabKey)) {
      return false;
    }
    // First tab where user logs in will be set as primary tab for managing SSE connection
    localStorage.setItem(SsePrimaryTabKey, _.now().toString());

    // Release ownership when tab closes, so that the next time another tab logs in/refreshes, it can claim itself as primary tab
    window.addEventListener('beforeunload', () => {
      localStorage.removeItem(SsePrimaryTabKey);
      // Close SSE connection when tab is closed
      this.closeConnection();
    });
    return true;
  }

  isSsePrimaryTab = (): boolean => this._isSsePrimaryTab;

  /**
   * If connection cannot be made, the system will continue to retry until successful.
   * On connection established, update notification store slice, isSSEConnected = true.
   * */
  openConnection() {
    // Check whether this tab is the primary tab which handles SSE connection initialization, if not, skip opening connection
    if (!this.isSsePrimaryTab()) {
      return;
    }
    // If connection is being established/already established, do nothing
    if (!_.isNil(this._eventSource) && (this._eventSource.readyState === EventSource.OPEN || this._eventSource.readyState === EventSource.CONNECTING)) {
      return;
    }
    // Cleanup SSE resources before creating a new connection
    if (!_.isNil(this._eventSource) && this._eventSource.readyState === EventSource.CLOSED) {
      this._cleanupSSEResources();
    }

    // For non paymaster user, server will return 403 status
    this._openConnection();

    // Client Side Retry logic
    // SSE connection should be maintained the entire time user is logged in.
    // The issue is that SSE will periodically disconnect as the http connection times out.
    // When that happens this._eventSource.readyState will be set to 0 or 2 (not 1).
    // Therefore, the system should periodically check if this._eventSource.readyState === 1, and when it is not, continue to retry

    // Check SSE connection status every 5 minutes and retry if not connected
    this._clientReconnectTimer = setInterval(() => {
      // Polling this API to check if user is still logged in
     this._authService.retrieveUser()
       .pipe(
         switchMap(() => {
           // If user is still logged in & SSE connection is closed, retry opening SSE connection
           if (this._eventSource?.readyState === EventSource.CLOSED) {
             this._openConnection();
           }
           return EMPTY;
         }),
         // Logging user out will clear paymaster SSE subscription on server side
         catchError(() => this._authService.logout()),
         takeWhile(() => this._isAlive)
       )
      .subscribe();
    }, 5 * 60 * 1000);

    // Avoid multiple retry timers or return if user is non paymaster
    // We can remove the second clause here to open SSE for all users. Without this check non paymaster users will poll retrieveUser API.
    const isPaymaster = getState<ClientConfig>(this._store, selectIsPaymaster);
    if (this._clientReconnectTimer || !isPaymaster) {
      return;
    }
  }

  /**
   * Does resource cleanup on client when user logs out
   */
  closeConnection() {
    this._cleanupSSEResources();
  }

  ngOnDestroy() {
    this._isAlive = false;
    this._cleanupSSEResources();
  }
}
